import { z } from "zod";

type ConfigShape = Record<string, z.ZodType>;

type StripDefault<Field extends z.ZodType> =
  Field extends z.ZodDefault<infer Inner> ? Inner : Field;

type OverrideShape<Shape extends ConfigShape> = {
  [Key in keyof Shape]: z.ZodOptional<StripDefault<Shape[Key]>>;
};

/**
 * The per-run override counterpart of a config namespace schema: every field
 * optional with its `.default(...)` stripped, so parsing yields exactly the
 * keys the override names and nothing else.
 *
 * `.partial()` alone cannot do this — it leaves each `ZodDefault` in place, so
 * `{}` parses into a fully-populated config and merging an override would
 * clobber every AppConfig-resolved value with a schema default. Field validators
 * (`.int().positive()`, enums) survive, and the result is strict, so a
 * malformed value or an unknown key still fails loudly.
 */
export function overrideSchema<Shape extends ConfigShape>(schema: {
  shape: Shape;
}): z.ZodObject<OverrideShape<Shape>> {
  const shape = Object.fromEntries(
    Object.entries(schema.shape).map(([key, field]) => [
      key,
      stripDefault(field).optional(),
    ]),
  ) as OverrideShape<Shape>;
  return z.strictObject(shape);
}

// ZodDefault declares its inner type as the core `$ZodType`, which has no
// builder methods; the instance always is a full ZodType, so the assertion
// only restores what the wrapper's declaration drops.
function stripDefault(field: z.ZodType): z.ZodType {
  return field instanceof z.ZodDefault
    ? stripDefault(field.def.innerType as z.ZodType)
    : field;
}
